import { useMemo } from 'react'

import { generateAerobicCapabilitiesReportBody } from '~/modules/aerobicCapabilities/services'
import { useAreobicCapabilities } from '~/modules/aerobicCapabilities/useAerobicCapabilities'
import { useAerobicPrescription } from '~/modules/aerobicPrescription/useAerobicPrescription'
import {
  objectiveOptions,
  tableData,
} from '~/modules/aerobicPrescription/variables'
import { useCoronarianRiskFactorFormula } from '~/modules/anamnesis/useCoronarianRiskFactorFormula'
import { evaluationReadOnlyAtom } from '~/modules/evalutation/data'
import { useAtom } from '~/observers/jotai'

import { Header } from './Header'

export const HemodynamicMeasures: React.FC = () => {
  const [evaluation] = useAtom(evaluationReadOnlyAtom)

  const { coronarianRiskFactorResult } =
    useCoronarianRiskFactorFormula(evaluation)

  const {
    highlightedCellsIndex,
    trainingVO2,
    trainingMETS,
    initialSessionDuration,
    minimumKgcalForTrainging,
    energyPerMinute,
    minorTrainingZone,
    majorTrainingZone,
  } = useAerobicPrescription(evaluation)

  const {
    basal,
    starter,
    moderatelyActive,
    active,
    vo2,
    classification,
    reserved,
    METSMax,
    foreseenVO2,
    highlightIndex,
  } = useAreobicCapabilities(evaluation)

  const aerobicPrescriptionActivity = useMemo(
    () =>
      objectiveOptions.find((o) => o.value === evaluation?.objectiveIndex)
        ?.label,
    [evaluation],
  )

  const reserveFrequencyLine = useMemo(() => {
    let finalText = ''
    const finalBogValues = []
    const finalAdaptedValue: number[] = []
    for (const index of highlightedCellsIndex) {
      const [text, borgValue, adaptedValue] = tableData[index]
      finalText = text + ''
      finalBogValues.push(borgValue)
      !finalAdaptedValue.includes(+adaptedValue) &&
        finalAdaptedValue.push(+adaptedValue)
    }

    return `${finalText} (Borg: ${finalBogValues.join(
      ' e ',
    )} Adaptada: ${finalAdaptedValue.join(' e ')})`
  }, [highlightedCellsIndex])

  return (
    <div className='flex flex-col px-8  py-7 w-full page-break'>
      <Header title='Medidas Hemodinâmicas, Capacidade e Prescrição Aeróbica' />

      {/* Section Medidas Hemodinâmicas */}
      <div className='flex border justify-center border-black bg-zinc-300 my-4'>
        <span className='font-bold text-sm '>Medidas Hemodinâmicas</span>
      </div>

      <div className='grid grid-cols-2 border-b border-black'>
        <div>
          <div className='flex justify-between text-sm my-4'>
            <span className='font-bold'>Frequência Cardíaca Máxima (bpm) </span>
            <span>{evaluation?.maxHeartFrequency}</span>
          </div>
          <div className='flex justify-between text-sm mb-4'>
            <span className='font-bold'>
              Frequência Cardíaca em Repouso (bpm){' '}
            </span>
            <span>{evaluation?.restingHeartFrequency}</span>
          </div>
        </div>
      </div>

      <div className='flex flex-col mt-4'>
        <div className='flex text-sm'>
          <span className='font-bold'>Pressão Sistólica (mmHg)</span>
          <span className='pl-6'>{evaluation?.systolicArterialPressure}</span>
        </div>

        <div className='flex text-sm'>
          <span className='font-bold'>Pressão Diastólica (mmHg)</span>
          <span className='pl-4'>{evaluation?.diastolicArterialPressure}</span>
        </div>

        <div className='flex text-sm mb-4'>
          <span className='font-bold'>Classificação</span>
          <span className='pl-28'>
            {coronarianRiskFactorResult.arterialPressureScore}
          </span>
        </div>
      </div>

      {/* Section Capacidade Aeróbia */}
      <div className='flex border justify-center border-black bg-zinc-300'>
        <span className='font-bold text-sm '>Capacidade Aeróbica</span>
      </div>

      <div className='grid grid-cols-printCapacidadeAerobia text-sm'>
        {/* Coluna 1 */}
        <div className='flex flex-col gap-2 mt-3'>
          <span className='font-bold'>Teste de Capacidade Aeróbica</span>

          <span>{evaluation?.aerobicCapabilitiesProtocol}</span>

          {generateAerobicCapabilitiesReportBody(evaluation).map(
            (row, index) => (
              <div
                key={`protocolAerobicRow-${index}`}
                className='flex justify-between pr-2'
              >
                <span className='font-bold'>{row[0]}</span>
                <span>{row[1]}</span>
              </div>
            ),
          )}

          <div className='mt-auto'>
            <span className='font-bold'>Taxa Metabólica Diária</span>
            <div className='mt-2 pr-2'>
              <table className='w-full table-auto border border-black'>
                <thead>
                  <tr>
                    <td rowSpan={4}>
                      <div className='flex flex-col items-center'>
                        <span className='font-bold'>Basal</span>
                        {/* <span>20180</span> */}
                        <span>{basal}</span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      data-highlight={highlightIndex === 0}
                      className='border border-black
                      border-t-0 data-[highlight=true]:bg-cyan-300 pl-2'
                    >
                      Iniciante
                    </td>

                    <td
                      data-highlight={highlightIndex === 0}
                      className='border border-black
                      border-t-0 data-[highlight=true]:bg-cyan-300 pl-2'
                    >
                      {starter}
                    </td>
                  </tr>
                  <tr>
                    <td
                      data-highlight={highlightIndex === 1}
                      className='border border-black
                      border-t-0 data-[highlight=true]:bg-cyan-300 pl-2'
                    >
                      Moderadamente Ativo
                    </td>
                    <td
                      data-highlight={highlightIndex === 1}
                      className='data-[highlight=true]:bg-cyan-300 pl-2'
                    >
                      {moderatelyActive}
                    </td>
                  </tr>
                  <tr>
                    <td
                      data-highlight={highlightIndex === 2}
                      className='data-[highlight=true]:bg-cyan-300 pl-2
                      border-x border-black'
                    >
                      Ativo
                    </td>
                    <td
                      data-highlight={highlightIndex === 2}
                      className='data-[highlight=true]:bg-cyan-300 pl-2
                      border-t border-black'
                    >
                      {active}
                    </td>
                  </tr>
                </thead>
              </table>
            </div>
          </div>
        </div>

        {/* Coluna 2 - section VO2 */}
        <div className='border border-black flex flex-col mt-14 p-2  relative pt-8'>
          <span className='absolute -top-2.5 font-bold left-4 bg-white px-2'>
            VO2
          </span>
          <div className='grid grid-cols-3'>
            <div className='flex flex-col space-y-4'>
              <div className='flex justify-between pr-3'>
                <span className='font-bold'>Medido</span>
                <span>{vo2.toFixed(2)}</span>
              </div>
              <div className='flex justify-between pr-3'>
                <span className='font-bold'>VO2 Previsto</span>
                <span>{foreseenVO2.toFixed(2)}</span>
              </div>
              <div className='flex justify-between pr-3'>
                <span className='font-bold'>VO2 alvo</span>
                <span>{evaluation?.targetVO2.toFixed(2)}</span>
              </div>
            </div>
            <div className='space-y-4'>
              <div>
                <span className='font-bold'>Classificação</span>
              </div>
              <div className='flex justify-between pr-4'>
                <span className='font-bold'>Reserva</span>
                <span>{reserved.toFixed(2)}</span>
              </div>
            </div>

            <div className='space-y-4'>
              <div>
                {/* <span>Muito Fraca (1/5)</span> */}
                <span>{classification.label}</span>
              </div>
              <div>
                <span className='font-bold'>METS Máximo : </span>

                <span>{METSMax.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Prescrição Aeróbia */}
      <div className='flex border justify-center border-black bg-zinc-300 my-4'>
        <span className='font-bold text-sm '>Prescrição Aeróbia</span>
      </div>

      <div className='grid grid-cols-2 text-sm border-b border-black'>
        {/* Coluna 1 */}
        <div className='flex flex-col space-y-3 pr-6 pb-4'>
          <span className='font-bold'>Cálculo de Zona Alvo</span>
          <span>Frequência de Reserva</span>
          <span className='font-bold'>Objetivo do trabalho aeróbio</span>
          <span>{aerobicPrescriptionActivity}</span>
          <span className='border bg-[#881337] text-xl text-white font-bold flex justify-center'>
            Zona Alvo no solo {minorTrainingZone} - {majorTrainingZone}
          </span>
        </div>

        {/* Coluna 2 */}
        <div className='flex flex-col space-y-3 pr-6'>
          <span className='font-bold'>Percepção Subjetiva de Esforço</span>
          <span>{reserveFrequencyLine}</span>

          <span className='font-bold'>Intensidade dentro da faixa</span>
          <span>{evaluation?.intensity}</span>
        </div>
      </div>

      <div className='grid grid-cols-3 text-sm'>
        {/* Coluna 1 */}
        <div className='flex flex-col space-y-3 pr-6 pt-4'>
          <span className='font-bold'>VO2 de Treino </span>
          <span>{trainingVO2}</span>
          <span className='font-bold '>
            Demanda energética mínima proposta(KCal)
          </span>
          <span>{evaluation?.proposedEnergy}</span>
        </div>

        {/* Coluna 2 */}
        <div className='flex flex-col space-y-3 pr-6 pt-4'>
          <span className='font-bold'>METS de Treino </span>
          <span>{trainingMETS}</span>
          <span className='font-bold'>Demanda energética proposta(KCal)</span>
          <span>{minimumKgcalForTrainging}</span>
        </div>

        {/* Coluna 3 */}
        <div className='flex flex-col space-y-3 pr-6 pt-4'>
          <span className='font-bold'>Duração Inicial da Sessão </span>
          <span>{initialSessionDuration}</span>
          <span className='font-bold whitespace-nowrap'>
            Demanda energética(KCal/Min)
          </span>
          <span>{energyPerMinute}</span>
        </div>
      </div>
    </div>
  )
}
